import { atom } from 'jotai';
import type { Dispatch, SetStateAction } from 'react';

import type { TeamMember } from '@/api/v4/team.api';
import type { ExampleCompany } from '@/features/explore/explore.state';
import type { UserRoleOption } from '@/features/settings/settings.state';
import type { FileDelete } from '@/helpers/files';
import type { VerificationMethod } from '@/user/verification-method/verification-method.type';
import type { ProjectListing } from '@/api/v4/projects.api';

interface Set2FAMethodModal {
  state: 'set2FAMethod';
  method: VerificationMethod;
}

interface ChangePasswordModal {
  state: 'changePassword';
}

interface AddCompanyModal {
  state: 'addCompany';
  projectId: string;
  onSuccess?: () => void;
}

interface AddCompanyToProjectsModal {
  state: 'addCompanyToProjects';
  companyName?: string;
}

interface CreateNewCompanyModal {
  state: 'createNewCompany';
  closeOnCreate?: boolean;
  defaultSearchPhrase?: string;
  defaultWebsite?: string;
  navigateToPageOnCreated?: boolean;
  onClose?: (createdCompanies: string[]) => Promise<void>;
}

interface AskToJoinFormModal {
  linkId?: string;
  organizationName?: string;
  spotlightLinkId?: string;
  state: 'askToJoinForm';
}

interface DeleteProjectListingModal {
  state: 'deleteProjectListing';
  listing: ProjectListing;
}

interface DeleteProjectModal {
  state: 'deleteProject';
  projectId: string;
}

interface DeleteTeamModal {
  state: 'deleteTeam';
  teamId: string;
  teamName: string;
}

interface DeleteTeamMemberModal {
  state: 'deleteTeamMember';
  user: TeamMember;
  onConfirm: () => void;
}

interface DuplicateProjectModal {
  state: 'duplicateProject';
}

interface ShareProjectModal {
  state: 'shareProject';
}

interface DealModal {
  state: 'deal';
  listingId: string;
  onClosed?: () => void;
  onSuccess?: (newStatusId: string) => void;
}

interface ShareCompanyModal {
  state: 'shareCompany';
  companyId: string;
}

interface RevokeInvitationModal {
  state: 'revokeInvitation';
  userId: number;
}

interface DeleteCompanyModal {
  state: 'deleteCompany';
  company: {
    id: string;
    name: string;
  };
}

interface ApproveProjectRequestModal {
  state: 'approveProjectRequest';
  requestId: string;
}

interface RejectProjectRequestModal {
  state: 'rejectProjectRequest';
  requestId: string;
}

interface DeleteCompaniesModal {
  state: 'deleteCompanies';
}

export type ITagsModalType = 'project' | 'team' | 'company';

interface ManageTagsModal {
  state: 'manageTags';
  type: ITagsModalType;
  entityId: string;
  refetchTags?: () => void;
}

interface DeleteTeamFileModal {
  state: 'deleteTeamFile';
  teamId: string;
  file: FileDelete;
}

interface DeleteProjectFileModal {
  state: 'deleteProjectFile';
  projectId: string;
  file: FileDelete;
}

interface ShareTeamFileModal {
  state: 'shareTeamFile';
  teamId: string;
}

interface DeleteCompanyFileModal {
  state: 'deleteCompanyFile';
  companyId: string;
  file: FileDelete;
}

interface ShareCompanyFileModal {
  state: 'shareCompanyFile';
  companyId: string;
}

interface InviteUserModal {
  state: 'inviteUser';
}

interface DeleteTemplateModal {
  state: 'deleteTemplate';
  templateId: string;
}

interface AddTeamMember {
  state: 'addTeamMember';
}

interface VerifyCompanyModal {
  state: 'verifyCompany';
  errorFields: string[];
}

interface ConfirmDeleteModal {
  confirmLabel?: string;
  description?: string;
  onConfirm: () => void;
  state: 'confirmDelete';
  title?: string;
}

interface ConfirmDiscardMeetingFormChangesModal {
  state: 'discardMeetingFormChanges';
  mainAction: () => void;
}

interface ConfirmDiscardChangesModal {
  state: 'confirmDiscardChanges';
  mainAction: () => void;
}

interface ConfirmExploreRoundTruncateModal {
  activeRoundNumber: number;
  latestRoundNumber: number;
  onConfirm: () => void;
  state: 'confirmExploreRoundTruncate';
}

interface ContactInnovationTeamModal {
  accountName: string;
  companyName: string;
  organizationId: string;
  state: 'contactInnovationTeam';
  useSpotlightInquiry?: boolean;
}

interface ChangeUserRoleModal {
  state: 'changeUserRole';
  userId: number;
  userDisplayName: string;
  currentRole: UserRoleOption;
  newRole: UserRoleOption;
  hasLinkedEntities: boolean;
  onCancel: () => void;
}

interface LoadCompaniesToProjectModal {
  state: 'loadCompaniesToProject';
  projectId: string;
}

interface CopyProjectModal {
  state: 'copyProjectModal';
  projectId: string;
}

interface CreateAccessTokenModal {
  state: 'createAccessToken';
}

interface SharedAssetsModal {
  state: 'showSharedAssets';
  email: string;
  userDisplayName: string;
}

interface SearchModal {
  state: 'search';
}

interface EnhanceProjectFormModal {
  state: 'enhanceProjectForm';
}

interface EnhanceProjectIntroModal {
  state: 'enhanceProjectIntro';
}

interface AutoEnhanceConfigModal {
  state: 'autoEnhanceConfig';
}

interface AutoEnhanceIntroModal {
  state: 'autoEnhanceIntro';
}

interface AccessTokenCreatedModal {
  rawKey: string;
  state: 'accessTokenCreated';
}

interface AddExampleCompaniesToExploreFormModal {
  state: 'addExampleCompaniesToExploreForm';
  exampleCompanies: ExampleCompany[];
  setExampleCompanies: Dispatch<SetStateAction<ExampleCompany[]>>;
}

interface AccountSwitchNotificationModal {
  accountName: string;
  entityType: string;
  previousAccountName: string;
  state: 'accountSwitchNotification';
}

type Modal =
  | Set2FAMethodModal
  | ChangePasswordModal
  | AddCompanyModal
  | AddCompanyToProjectsModal
  | AddTeamMember
  | CopyProjectModal
  | CreateAccessTokenModal
  | CreateNewCompanyModal
  | DeleteProjectListingModal
  | DeleteProjectModal
  | LoadCompaniesToProjectModal
  | DeleteTeamModal
  | DeleteTeamMemberModal
  | DuplicateProjectModal
  | ShareProjectModal
  | ShareCompanyModal
  | DealModal
  | ManageTagsModal
  | DeleteCompanyModal
  | DeleteCompaniesModal
  | ApproveProjectRequestModal
  | RejectProjectRequestModal
  | DeleteTeamFileModal
  | DeleteProjectFileModal
  | ShareTeamFileModal
  | DeleteCompanyFileModal
  | ShareCompanyFileModal
  | InviteUserModal
  | DeleteTemplateModal
  | VerifyCompanyModal
  | ConfirmDeleteModal
  | ConfirmDiscardMeetingFormChangesModal
  | ChangeUserRoleModal
  | SharedAssetsModal
  | SearchModal
  | EnhanceProjectIntroModal
  | EnhanceProjectFormModal
  | AutoEnhanceIntroModal
  | AutoEnhanceConfigModal
  | RevokeInvitationModal
  | AddExampleCompaniesToExploreFormModal
  | ConfirmDiscardChangesModal
  | ConfirmExploreRoundTruncateModal
  | ContactInnovationTeamModal
  | AskToJoinFormModal
  | AccessTokenCreatedModal
  | AccountSwitchNotificationModal
  | null;

export const modalState = atom<Modal>(null);
